import { clamp, parseInteger } from './utils';

/**
 * Updates the visual fill of a range input track.
 *
 * @param {HTMLInputElement} slider Range input element.
 * @return {void}
 */
const updateSliderTrack = ( slider ) => {

	const min = Number.parseFloat( slider.min ) || 0;
	const max = Number.parseFloat( slider.max ) || 100;
	const value = Number.parseFloat( slider.value ) || min;
	const range = max - min || 1;
	const percentage = clamp( ( ( value - min ) / range ) * 100, 0, 100 );
	const fillColor = '#2c67e8';
	const trackColor = '#eff4ff';

	slider.style.background = `linear-gradient(to right, ${ fillColor } ${ percentage }%, ${ fillColor } ${ percentage }%, ${ trackColor } ${ percentage }%, ${ trackColor } 100%)`;

};

/**
 * Returns the maximum allowed input value.
 *
 * @param {HTMLInputElement} input  Number input element.
 * @param {HTMLInputElement} slider Related slider element.
 * @return {number} Maximum allowed value.
 */
const getMaxValue = ( input, slider ) => {

	if ( input.max ) {

		return parseInteger( input.max, Number.MAX_SAFE_INTEGER );

	}

	if ( slider?.max ) {

		return parseInteger( slider.max, Number.MAX_SAFE_INTEGER );

	}

	return Number.MAX_SAFE_INTEGER;

};

/**
 * Binds a range slider with its paired number input.
 *
 * @param {Object} options Configuration object.
 * @param {HTMLInputElement|null} options.slider   Range input element.
 * @param {HTMLInputElement|null} options.input    Number input element.
 * @param {Function} options.onChange              Callback fired after value change.
 * @return {void}
 */
export const bindSliderPair = ( { slider, input, onChange } ) => {

	const stepper = input?.closest( '.b-pricing-calculator__stepper' ) || null;
	const decrementButton = stepper?.querySelector( '[data-step-action="decrement"]' ) || null;
	const incrementButton = stepper?.querySelector( '[data-step-action="increment"]' ) || null;

	/**
	 * Syncs disabled state of stepper buttons against current min/max bounds.
	 *
	 * @return {void}
	 */
	const updateStepperState = () => {

		if ( ! input ) {

			return;

		}

		const minValue = parseInteger( input.min, 1 );
		const maxValue = getMaxValue( input, slider );
		const currentValue = clamp( parseInteger( input.value, minValue ), minValue, maxValue );

		if ( decrementButton ) {

			decrementButton.disabled = currentValue <= minValue;

		}

		if ( incrementButton ) {

			incrementButton.disabled = currentValue >= maxValue;

		}

	};

	/**
	 * Normalizes a value, updates both controls, and triggers onChange.
	 *
	 * @param {number} rawValue Value to apply.
	 * @return {void}
	 */
	const applyValue = ( rawValue ) => {

		if ( ! input ) {

			return;

		}

		const minValue = parseInteger( input.min, 1 );
		const maxValue = getMaxValue( input, slider );
		const value = clamp( rawValue, minValue, maxValue );

		input.value = String( value );

		if ( slider ) {

			slider.value = String( value );
			slider.setAttribute( 'aria-valuenow', slider.value );
			updateSliderTrack( slider );

		}

		updateStepperState();
		onChange();

	};

	if ( slider ) {

		updateSliderTrack( slider );

		slider.addEventListener( 'input', () => {

			updateSliderTrack( slider );
			slider.setAttribute( 'aria-valuenow', slider.value );

			if ( input ) {

				input.value = slider.value;

			}

			updateStepperState();
			onChange();

		} );

	}

	if ( ! input ) {

		return;

	}

	input.addEventListener( 'input', () => {

		if ( input.value === '' ) {

			return;

		}

		const minValue = parseInteger( input.min, 1 );
		applyValue( parseInteger( input.value, minValue ) );

	} );

	decrementButton?.addEventListener( 'click', () => {

		applyValue( parseInteger( input.value, 1 ) - 1 );

	} );

	incrementButton?.addEventListener( 'click', () => {

		applyValue( parseInteger( input.value, 1 ) + 1 );

	} );

	updateStepperState();

};

/**
 * Binds a standalone bundle number input.
 *
 * @param {Object} options Configuration object.
 * @param {HTMLInputElement|null} options.input    Number input element.
 * @param {Function} options.onChange              Callback fired after value change.
 * @return {void}
 */
export const bindBundleInput = ( { input, onChange } ) => {

	if ( ! input ) {

		return;

	}

	input.addEventListener( 'input', () => {

		if ( input.value === '' ) {

			return;

		}

		const minValue = parseInteger( input.min, 0 );
		const maxValue = input.max
			? parseInteger( input.max, Number.MAX_SAFE_INTEGER )
			: Number.MAX_SAFE_INTEGER;
		const value = clamp( parseInteger( input.value, minValue ), minValue, maxValue );

		input.value = String( value );

		onChange();

	} );

};
